import { useCallback, useContext, useEffect, useState } from "react";

import { DealDetailsFormValues, SelectData } from "../../../../models";
import { searchOrganisationUsers, searchPipelineDeals } from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { NewDealJourneyContext } from "../NewDealJourneyContext";

const dealSourceSelectData: SelectData = [
  { key: "Supplier led", value: "Supplier led" },
  { key: "Buyer led", value: "Buyer led" },
  { key: "Internal", value: "Internal" },
  { key: "Directory", value: "Directory" },
];

interface UseDealDetailsReturnData {
  errors: ServiceError[] | undefined;
  formValues: DealDetailsFormValues;
  dealPipelinesSelectData: SelectData | undefined;
  organisationUsersSelectData: SelectData | undefined;
  dealSourceSelectData: SelectData;
  handleFieldChange: <K extends keyof DealDetailsFormValues>(field: K) => (value: DealDetailsFormValues[K]) => void;
}

export const useDealDetails = (): UseDealDetailsReturnData => {
  const { currentOrganisationUuid } = useAuth();
  const { errors, formValues, handleFieldChange } = useContext(NewDealJourneyContext);

  const [organisationUsersSelectData, setOrganisationUsersSelectData] = useState<SelectData>();
  const [dealPipelinesSelectData, setDealPipelinesSelectData] = useState<SelectData>();

  const getAndSetOrganisationsUsers = useCallback(async (organisationUuid: string) => {
    const res = await searchOrganisationUsers({
      organisationUuid,
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 50,
      },
    });

    if (res.status === Status.Success && res.data) {
      setOrganisationUsersSelectData(res.data.results.map((x) => ({ key: x.user.uuid, value: x.user.fullName })));
    }
  }, []);

  const getAndSetDealPipelines = useCallback(
    async (organisationUuid: string) => {
      const res = await searchPipelineDeals({
        organisationUuid,
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 50,
        },
      });

      if (res.status === Status.Success && res.data) {
        setDealPipelinesSelectData(res.data.aspects.pipelines.map((x) => ({ key: x.uuid, value: x.name })));
        handleFieldChange("dealPipelineUuid")(res.data.aspects.pipelines[0].uuid);
      }
    },
    [handleFieldChange]
  );

  useEffect(() => {
    if (currentOrganisationUuid) {
      getAndSetOrganisationsUsers(currentOrganisationUuid);
      getAndSetDealPipelines(currentOrganisationUuid);
    }
  }, [currentOrganisationUuid]);

  return {
    errors,
    formValues,
    dealPipelinesSelectData,
    organisationUsersSelectData,
    dealSourceSelectData,
    handleFieldChange,
  };
};
